import React, {CSSProperties, useMemo} from 'react';
import {HeadFC, PageProps, graphql, navigate} from 'gatsby';
import {PageContextI18n} from '@src/components/general/WrapPageElement/PageContext';
import {useTranslation} from 'gatsby-plugin-react-i18next';
import useGetFooter from '@src/Hooks/useGetFooter';
import PageHead from '@src/components/general/PageHead/PageHead';
import Metrics from 'design-system/src/Theme/Metrics';
import Button from 'design-system/src/Components/Button/Button';
import useIsLoggedIn from '@src/Providers/AuthProvider/useIsLoggedIn';
import {
  buildNativeAppUrl,
  buildStoreFallbackUrl,
  detectPlatform,
  getDeepLinkConfig,
  openDeepLink,
  resolveDeepLinkTarget,
} from '@src/utils/deepLinkUtils';

interface ServerData {
  props: {
    image: {
      message: string;
      status: string;
    };
  };
}

const actionGroupStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 24,
  padding: 20,
  alignItems: 'center',
};

const buttonStyle: CSSProperties = {
  paddingHorizontal: Metrics.horizontalScale(27),
};

const appOnlyContainerStyle: CSSProperties = {
  minHeight: '100vh',
  padding: '48px 20px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background:
    'linear-gradient(180deg, rgba(17,18,22,0.92) 0%, rgba(17,18,22,1) 100%)',
};

const appOnlyCardStyle: CSSProperties = {
  width: '100%',
  maxWidth: 560,
  background: '#111216',
  border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: 8,
  padding: '32px 24px',
  display: 'flex',
  flexDirection: 'column',
  gap: 20,
  alignItems: 'center',
  textAlign: 'center',
};

const DeepLinkPage = (
  props: PageProps<any, PageContextI18n, any, ServerData | undefined>,
) => {
  const {params} = props;
  const targetPath = params['*'] || '';
  const deepLinkTarget = useMemo(
    () => resolveDeepLinkTarget(targetPath, props.pageContext.language),
    [props.pageContext.language, targetPath],
  );
  const normalizedTargetPath = deepLinkTarget.normalizedPath;
  const appTargetPath = deepLinkTarget.nativePath;
  const isAppWebRoute = deepLinkTarget.isAppWebRoute;
  const {isLoggedIn} = useIsLoggedIn();
  const {t} = useTranslation('translation');
  const footerData = useGetFooter();

  const isMobilePlatform = detectPlatform() !== 'unknown';
  const appOnlyTitle = useMemo(() => {
    if (!normalizedTargetPath) {
      return 'Open in Trace Plus';
    }

    return normalizedTargetPath
      .split(/[/?#]/)[0]
      .split('/')
      .filter(Boolean)
      .map(segment =>
        segment
          .replace(/[-_]+/g, ' ')
          .replace(/\b\w/g, letter => letter.toUpperCase()),
      )
      .join(' / ');
  }, [normalizedTargetPath]);

  const appOnlyDescription = normalizedTargetPath
    ? `Continue in the Trace Plus app to open "${normalizedTargetPath}".`
    : 'Continue in the Trace Plus app from the default landing route.';

  // Store URLs with deep link parameters
  const getStoreUrlWithDeepLink = () => {
    return buildStoreFallbackUrl({
      path: targetPath,
      language: props.pageContext.language,
      platform: detectPlatform(),
      huaweiRedirectUrl: footerData?.huaweiRedirectUrl,
    });
  };

  const handleGetApp = () => {
    openDeepLink({
      path: appTargetPath,
      language: props.pageContext.language,
      isLoggedIn,
      iosFallback: getStoreUrlWithDeepLink(),
      androidFallback: getStoreUrlWithDeepLink(),
      useOneLink: true,
      attribution: {
        pid: 'web',
        campaign: 'p_handoff_page',
        af_sub1: props.pageContext.language,
        af_sub2: appTargetPath,
      },
    });
  };

  const handleContinueToWeb = () => {
    if (deepLinkTarget.webPath) {
      navigate(deepLinkTarget.webPath);
    }
  };

  return (
    <div>
      <div style={appOnlyContainerStyle}>
        <div style={appOnlyCardStyle}>
          <h1
            style={{
              margin: 0,
              color: '#ffffff',
              fontSize: 32,
              lineHeight: 1.2,
            }}>
            {appOnlyTitle}
          </h1>
          <p
            style={{
              margin: 0,
              color: 'rgba(255,255,255,0.72)',
              fontSize: 16,
              lineHeight: 1.6,
              maxWidth: 420,
            }}>
            {appOnlyDescription}
          </p>
          <div style={{...actionGroupStyle, padding: 0}}>
            <Button
              title={t('Continue to mobile app') || 'Continue to mobile app'}
              variant="contained"
              onPress={handleGetApp}
              style={buttonStyle}
            />
            {isAppWebRoute ? (
              <Button
                title={t('Continue to web') || 'Continue to web'}
                variant="contained"
                onPress={handleContinueToWeb}
                style={buttonStyle}
              />
            ) : null}
            {!isMobilePlatform ? (
              <p
                style={{
                  margin: 0,
                  color: 'rgba(255,255,255,0.56)',
                  fontSize: 13,
                  lineHeight: 1.5,
                  maxWidth: 360,
                }}>
                This route is available in the mobile app. Open it on your phone
                to continue.
              </p>
            ) : null}
          </div>
        </div>
      </div>
      <h1 style={{display: 'none'}}>{appOnlyTitle || 'Trace Plus'}</h1>
      <p style={{display: 'none'}}>
        {appOnlyDescription || 'Continue in the Trace Plus app.'}
      </p>
    </div>
  );
};

DeepLinkPage.layoutComponent = 'DefaultLayout';

export const query = graphql`
  query DeepLinkPage($language: String!) {
    locales: allLocale(filter: {language: {eq: $language}}) {
      edges {
        node {
          ns
          data
          language
        }
      }
    }

    umbraco {
      allLandingPage(first: 1, culture: $language) {
        items {
          id
          __typename
          contentTypeAlias
          ...LandingPageFragment
        }
      }
    }
  }
`;

// export async function getServerData() {
//   const res = await fetch(`https://dog.ceo/api/breeds/image/random`);
//   const data = await res.json();

//   return {
//     props: {
//       image: data,
//     },
//   };
// }

export const Head: HeadFC<
  Queries.DeepLinkPageQuery,
  ServerData | undefined
> = headProps => {
  const {
    data: {
      umbraco: {allLandingPage},
    },
    location,
  } = headProps;
  const targetPath =
    location.pathname.split('/p/')[1]?.replace(/^\/+|\/+$/g, '') || '';
  const appOnlyTitle = targetPath
    ? `Open ${targetPath} in Trace Plus`
    : 'Open in Trace Plus';
  const appOnlyDescription = targetPath
    ? `Continue in the Trace Plus app to open "${targetPath}".`
    : 'Continue in the Trace Plus app.';
  const title = allLandingPage?.items?.[0]?.metaTitle || appOnlyTitle;
  const description =
    allLandingPage?.items?.[0]?.metaDescription || appOnlyDescription;
  const image = allLandingPage?.items?.[0]?.cover?.content?.image?.url || '';
  const {androidPackageName, iosAppStoreId, siteUrl} = getDeepLinkConfig();
  const appTargetPath = resolveDeepLinkTarget(
    targetPath,
    headProps.pageContext.language,
  ).nativePath;
  const pageUrl = `${siteUrl}${location.pathname}`;
  const video = '';
  const appUrl = buildNativeAppUrl(appTargetPath);
  const iosAppName = 'Trace Plus';
  const androidAppName = 'Trace Plus';
  return (
    <PageHead
      title={allLandingPage?.items?.[0]?.metaTitle || undefined}
      description={allLandingPage?.items?.[0]?.metaDescription || undefined}
      {...headProps}>
      <title key="title">{title}</title>
      <meta key="meta-description" name="description" content={description} />
      <meta key="meta-og-title" property="og:title" content={title} />
      <meta
        key="meta-og-description"
        property="og:description"
        content={description}
      />
      <meta key="meta-og-url" property="og:url" content={pageUrl} />
      <meta key="meta-og-image" property="og:image" content={image} />
      <meta key="meta-og-video" property="og:video" content={video} />
      <meta
        key="meta-og-video-secure"
        property="og:video:secure_url"
        content={video}
      />
      <meta
        key="meta-og-video-type"
        property="og:video:type"
        content="video/mp4"
      />
      <meta key="meta-al-ios-url" property="al:ios:url" content={appUrl}></meta>
      <meta
        key="meta-al-ios-store"
        property="al:ios:app_store_id"
        content={iosAppStoreId}></meta>
      <meta
        key="meta-al-ios-name"
        property="al:ios:app_name"
        content={iosAppName}></meta>
      <meta
        key="meta-al-android-url"
        property="al:android:url"
        content={appUrl}></meta>
      <meta
        key="meta-al-android-package"
        property="al:android:package"
        content={androidPackageName}></meta>
      <meta
        key="meta-al-android-name"
        property="al:android:app_name"
        content={androidAppName}></meta>
    </PageHead>
  );
};

export default DeepLinkPage;
